import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { PROJECTS } from "@/data/content";
import { ArrowUpRight } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useContent } from "@/hooks/useContent";
import { resolveMedia } from "@/lib/media";

const filters = ["All", "Completed", "Ongoing", "Future Concept"];

const Projects = () => {
  useDocumentTitle("Projects");
  const [f, setF] = useState("All");
  const { items: PROJ } = useContent("projects", PROJECTS);
  const items = f === "All" ? PROJ : PROJ.filter((p) => p.status === f);

  return (
    <div data-testid="projects-page">
      <PageHeader
        eyebrow="Projects"
        title="Innovation, in the wild."
        description="A curated look at completed programs, ongoing research and forward-looking concepts across our engineering practice."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Projects" }]}
      />

      <section className="py-14 lg:py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10">
            {filters.map((x) => (
              <button
                key={x}
                data-testid={`project-filter-${x.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setF(x)}
                className={`text-xs uppercase tracking-widest px-3.5 py-2 rounded-full border transition-colors ${
                  f === x
                    ? "bg-[#0B1B3D] text-white border-[#0B1B3D]"
                    : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1B3D]"
                }`}
              >
                {x}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p, i) => (
              <div
                key={p.id || p.title}
                data-testid={`project-card-${i}`}
                className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={resolveMedia(p.image_url || p.image)}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3D]/60 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <span
                      className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                        p.status === "Completed"
                          ? "bg-white text-emerald-700 border-emerald-200"
                          : p.status === "Ongoing"
                          ? "bg-white text-amber-700 border-amber-200"
                          : "bg-white text-sky-700 border-sky-200"
                      }`}
                    >
                      {p.status}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest bg-white/90 border border-slate-200 text-slate-700 px-2.5 py-1 rounded-full">
                      {p.domain}
                    </span>
                  </div>
                  <div className="absolute bottom-3 right-3 text-[10px] uppercase tracking-widest bg-white/90 border border-slate-200 text-slate-700 px-2.5 py-1 rounded-full font-mono-akyvia">
                    {p.year}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-semibold text-[#0B1B3D] leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-amber-600 font-medium">
                    Case study coming soon
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Partnership"
        title="Have a research project in mind?"
        description="We collaborate with universities, industry and mission programs on cutting-edge engineering programs."
      />
      <div className="py-24" />
    </div>
  );
};

export default Projects;
